import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { SmartParentProgramPopup } from '../../components/Popups';
import { SmartAlbumListTemplate } from '../../components/Templates';
import { AlbumCellProps, AlbumNodeProps } from '../../components/Molecules';

import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import { useUserStore } from '../../stores/UserStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { AlbumListType, SpaceListType } from '../../models/SpaceStore';
import { arrayEqual } from '../../utils/array';

interface State {
  managedClassroomId: string | undefined | null;
}

const fetchManagedSpaces: FetchListFn = (cursor, signal) =>
  useSpaceStore.getState().fetchManagedSpaces(cursor, { signal });

const SmartAlbumListScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const activeUserId = useSessionStore((store) => store.activeUserId);

  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  const [state, setState] = useState<State>({
    managedClassroomId: undefined,
  });

  useEffect(() => {
    if (!activeUserId || !userProfile) {
      navigate('/start');
    }
  }, [activeUserId, navigate, userProfile]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        userProfile?.worksForJoyajoy ||
        (userAccount?.managedClassroomExpiredAt &&
          userAccount?.managedClassroomExpiredAt > new Date())
      ) {
        // do nothing
      } else {
        navigate('/smart-parents');
      }
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [
    navigate,
    userAccount?.managedClassroomExpiredAt,
    userProfile?.worksForJoyajoy,
  ]);

  const managedSpaceField = useSpaceStoreListField(
    'spaces',
    SpaceListType.Managed,
  );
  const {
    isFinished: isFinishedManagedSpace,
    fetchAll: fetchAllManagedSpaces,
  } = useFetchList(managedSpaceField, fetchManagedSpaces);

  useEffect(() => {
    fetchAllManagedSpaces().catch(console.error);
  }, [fetchAllManagedSpaces]);

  const managedCollection = useSpaceStore((store) => {
    return store.getSpaces(SpaceListType.Managed);
  }, arrayEqual(shallow));

  useEffect(() => {
    const managedClassroom = managedCollection[0];
    if (managedClassroom) {
      console.log('managed classroom id', managedClassroom.classroomId);
      setState((s) => ({
        ...s,
        managedClassroomId: managedClassroom.classroomId,
      }));
    } else {
      if (isFinishedManagedSpace) {
        setState((s) => ({
          ...s,
          managedClassroomId: null,
        }));
      }
    }
  }, [isFinishedManagedSpace, managedCollection]);

  useEffect(() => {
    if (state.managedClassroomId === null) {
      navigate('/smart-parents');
    }
  }, [navigate, state.managedClassroomId]);

  const albumNormalField = useSpaceStoreListField(
    'albums',
    AlbumListType.Normal,
  );

  const fetchAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchAlbums(
        {
          type: AlbumListType.Normal,
          classroomId: state.managedClassroomId ?? '',
        },
        cursor,
      ),
    [state.managedClassroomId],
  );

  const {
    fetchMore: fetchMoreAlbums,
    refresh: refreshAlbums,
    isFetchingMore,
    isFinished,
  } = useFetchList(albumNormalField, fetchAlbums);

  useEffect(() => {
    if (state.managedClassroomId) {
      refreshAlbums().catch(console.error);
    }
  }, [refreshAlbums, state.managedClassroomId]);

  const loadMoreItems = useCallback(() => {
    fetchMoreAlbums().catch(console.error);
  }, [fetchMoreAlbums]);

  const handleScroll = useCallback(() => {
    const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
    if (
      scrollTop + clientHeight >= scrollHeight - 400 && // 152 is the footer
      !isFetchingMore &&
      !isFinished
    ) {
      loadMoreItems();
    }
  }, [isFetchingMore, isFinished, loadMoreItems]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const onClickAlbum = useCallback(
    (classroomId: string, gameSetId: string) => {
      navigate(`/club/${classroomId}/journey/${gameSetId}`);
    },
    [navigate],
  );

  const albums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(AlbumListType.Normal);
  }, arrayEqual(shallow));

  const albumData = useMemo<AlbumCellProps[]>(() => {
    const managedClassroom = managedCollection[0];
    return albums.map((album) => {
      return {
        album: album,
        albumClassroom: managedClassroom!,
        onClick: () => {
          onClickAlbum(managedClassroom!.classroomId, album.gameSetId);
        },
      };
    });
  }, [albums, managedCollection, onClickAlbum]);

  const albumRecentField = useSpaceStoreListField(
    'albums',
    AlbumListType.Recent,
  );

  const fetchRecentAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchRecentAlbums(
        {
          classroomId: state.managedClassroomId ?? '',
        },
        cursor,
      ),
    [state.managedClassroomId],
  );

  const { refresh: refreshRecentAlbums } = useFetchList(
    albumRecentField,
    fetchRecentAlbums,
  );

  useEffect(() => {
    if (state.managedClassroomId) {
      refreshRecentAlbums().catch(console.error);
    }
  }, [refreshRecentAlbums, state.managedClassroomId]);

  const recentAlbums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(AlbumListType.Recent);
  }, arrayEqual(shallow));

  const recentAlbumData = useMemo<AlbumNodeProps[]>(() => {
    const managedClassroom = managedCollection[0];
    return recentAlbums.map((album) => {
      return {
        album: album,
        onClick: () => {
          onClickAlbum(managedClassroom!.classroomId, album.gameSetId);
        },
      };
    });
  }, [managedCollection, onClickAlbum, recentAlbums]);

  const onClickCreate = useCallback(() => {
    if (state.managedClassroomId) {
      useDraftStore.getState().setManagedClassroomId(state.managedClassroomId);
    }
    navigate('/create/memory-game');
  }, [navigate, state.managedClassroomId]);

  const onClidkDetailPopup = useCallback(() => {
    hidePopup();
    navigate('/home');
  }, [hidePopup, navigate]);

  const onClickInfo = useCallback(() => {
    showPopup(
      <SmartParentProgramPopup
        onPrimary={onClidkDetailPopup}
        onCancel={hidePopup}
      />,
    );
  }, [hidePopup, onClidkDetailPopup, showPopup]);

  return userProfile ? (
    <SmartAlbumListTemplate
      userProfile={userProfile}
      managedClassroomExpiredAt={userAccount!.managedClassroomExpiredAt!}
      level={userProfile.exp.level}
      progress={userProfile.exp.progress}
      currentExp={userProfile.exp.total}
      totalExp={userProfile.exp.totalNextLevelRequirement}
      socialExp={userProfile.exp.socialContribution.total}
      knowledgeExp={userProfile.exp.knowledge.total}
      recentAlbumData={recentAlbumData}
      albumData={albumData}
      onClickInfo={onClickInfo}
      onClickCreate={onClickCreate}
    />
  ) : null;
};

export default SmartAlbumListScreen;
